// src/hooks/useDashboardManutencao.js
import { useEffect, useMemo, useRef, useState } from "react";

import { db } from "../config/firebase";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { isStatusBloqueado } from "../config/status";

const DASHBOARD_CACHE_TTL_MS = 120000;
const dashboardCache = new Map();

function tsToDate(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts === "object" && typeof ts.toDate === "function") return ts.toDate();
  if (typeof ts.seconds === "number") return new Date(ts.seconds * 1000);
  return null;
}

function hoursBetween(a, b) {
  const da = tsToDate(a);
  const dbb = tsToDate(b);
  if (!da || !dbb) return 0;
  const ms = dbb.getTime() - da.getTime();
  if (!Number.isFinite(ms)) return 0;
  return Math.max(0, ms / 36e5);
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}

function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

function pctDelta(curr, prev) {
  const a = Number(curr) || 0;
  const b = Number(prev) || 0;
  if (b === 0) return a === 0 ? 0 : 100;
  return ((a - b) / b) * 100;
}

function pickEndTs(os) {
  return os.atualizadoEm || os.updatedAt || os.updatedEm || os.finalizadoEm || null;
}

function isMaquinaParada(os) {
  return String(os?.maquinaParou || "").toLowerCase().trim() === "sim";
}

function isMaquinaParadaAgora(os) {
  // Derivado de atendimentos (para OS antigas que não têm os novos campos)
  if (os?.__derivedParadaFim) return false;

  // Se existe timestamp de volta/encerramento, a máquina não está parada agora
  if (os?.maquinaVoltouRodarEm || os?.maquinaParadaEncerradaEm) return false;

  // Se o último atendimento registrou explicitamente que NÃO continua parada, não está parada agora
  const ult = String(os?.maquinaContinuaParadaUltimo || "").toLowerCase().trim();
  if (["nao", "não", "n", "false", "0", "no"].includes(ult)) return false;

  // Novo campo (fix): controla o estado atual da parada, mesmo com a OS aberta
  if (typeof os?.maquinaParadaAtiva === "boolean") return os.maquinaParadaAtiva;

  // Fallback para dados antigos
  return isMaquinaParada(os);
}

function pickParadaInicio(os) {
  return os?.maquinaParouEm || os?.criadoEm || null;
}

function pickParadaFim(os, refNow) {
  // Derivado de atendimentos (para OS antigas que não têm os novos campos)
  if (os?.__derivedParadaFim) return os.__derivedParadaFim;

  // Se a máquina voltou a rodar antes de fechar a OS, para de contar aqui
  if (os?.maquinaVoltouRodarEm) return os.maquinaVoltouRodarEm;
  // Campo novo: encerra a parada mesmo mantendo OS aberta
  if (os?.maquinaParadaEncerradaEm) return os.maquinaParadaEncerradaEm;

  // Se o último atendimento registrou explicitamente que NÃO continua parada, não deve continuar contando
  const ult = String(os?.maquinaContinuaParadaUltimo || "").toLowerCase().trim();
  if (["nao", "não", "n", "false", "0", "no"].includes(ult)) {
    // Preferir o último update conhecido (melhor do que seguir contando até agora)
    return pickEndTs(os) || refNow;
  }

  // Se o estado atual da parada está desativado (mesmo com OS aberta), não deve continuar contando
  if (typeof os?.maquinaParadaAtiva === "boolean" && os.maquinaParadaAtiva === false) {
    return pickEndTs(os) || refNow;
  }

  // Se a OS fechou, usa o fim da OS
  if (isStatusBloqueado(os?.statusNome)) return pickEndTs(os);
  // Ainda aberta e parada ativa: conta até agora
  return refNow;
}

function isAguardandoMaterial(os) {
  return String(os?.statusNome || "").toLowerCase().includes("aguardando material");
}

function isFechadaEm(os, refDate) {
  // Aproximação: se status bloqueado e atualizadoEm existe <= refDate
  if (!isStatusBloqueado(os?.statusNome)) return false;
  const endTs = pickEndTs(os);
  const endDate = tsToDate(endTs);
  if (!endDate) return false;
  return endDate.getTime() <= refDate.getTime();
}

function isAbertaEm(os, refDate) {
  // OS está "aberta" em refDate se foi criada antes e ainda não fechou até refDate
  const criado = tsToDate(os?.criadoEm);
  if (!criado) return false;
  if (criado.getTime() > refDate.getTime()) return false;
  if (!isStatusBloqueado(os?.statusNome)) return true;
  // se está bloqueado, só é "aberta" se fechou depois do refDate (ou não tem fim)
  const endTs = pickEndTs(os);
  const endDate = tsToDate(endTs);
  if (!endDate) return true;
  return endDate.getTime() > refDate.getTime();
}

export function useDashboardManutencao({ grupoId } = {}) {
  const canLoad = useMemo(() => !!grupoId, [grupoId]);
  const requestRef = useRef(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [kpis, setKpis] = useState({
    paradasHorasMes: 0,
    paradasHorasMesPrev: 0,
    maquinasParadasAgora: 0,
    maquinasParadasAgoraPrev: 0,
    osAbertasAgora: 0,
    osAbertasAgoraPrev: 0,
    // Quantidade de OS criadas no mês (independente de status)
    osAbertasMes: 0,
    osAbertasMesPrev: 0,
    osFinalizadasMes: 0,
    osFinalizadasMesPrev: 0,
    aguardandoMaterialAgora: 0,
    aguardandoMaterialAgoraPrev: 0,
    mtbfHorasMes: null,
    mttrHorasMes: null,
    maiorParadaHorasMes: 0,
    maquinaMaisCriticaMes: "-",
  });

  const [topOsAbertas, setTopOsAbertas] = useState([]);

  useEffect(() => {
    if (!canLoad) return;

    async function run() {
      const requestId = requestRef.current + 1;
      requestRef.current = requestId;

      const isStale = () => requestRef.current !== requestId;
      const cacheKey = String(grupoId || "");
      const cached = dashboardCache.get(cacheKey);

      if (cached && Date.now() - cached.at < DASHBOARD_CACHE_TTL_MS) {
        if (!isStale()) {
          setError("");
          setTopOsAbertas(cached.topOsAbertas);
          setKpis(cached.kpis);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError("");

      try {
        const ref = collection(db, "ordensServico");
        const q = query(ref, where("grupoId", "==", grupoId));
        // Máquinas-chave (para MTBF Global)
        const maqRef = collection(db, "maquinas");
        const qMaq = query(maqRef, where("grupoId", "==", grupoId), where("maquinaChave", "==", true), limit(5000));

        const [snap, maqSnap] = await Promise.all([getDocs(q), getDocs(qMaq)]);
        if (isStale()) return;

        const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const maquinasChave = maqSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const chaveIds = new Set(maquinasChave.map((m) => String(m.id)));
        const chaveCodigos = new Set(maquinasChave.map((m) => String(m.codigo || "").trim()).filter(Boolean));
        const chaveNomes = new Set(maquinasChave.map((m) => String(m.nome || "").trim().toLowerCase()).filter(Boolean));

        const isOsDeMaquinaChave = (os) => {
          const id = os?.maquinaId || os?.maquina?.id || os?.maquinaRefId || null;
          const cod = os?.maquinaCodigo || os?.maquinaCod || os?.codigoMaquina || null;
          const nome = os?.maquinaNome || os?.maquinaNomeSnapshot || null;

          if (id && chaveIds.has(String(id))) return true;
          if (cod && chaveCodigos.has(String(cod).trim())) return true;
          if (nome && chaveNomes.has(String(nome).trim().toLowerCase())) return true;
          return false;
        };


        const baseLista = lista.filter(isOsDeMaquinaChave);


        // ------------------------------------------------------------
        // Compatibilidade com OS antigas (sem os campos novos de parada)
        // Busca o último atendimento por OS e usa como "fonte" para
        // inferir o fim da parada quando "Máquina continua parada" = NÃO.
        // Obs: não escreve de volta no Firestore; é só para cálculo.
        // ------------------------------------------------------------
        const requerFallbackAtendimentos = baseLista.some((os) => {
          if (!isMaquinaParada(os)) return false;
          const possuiFimExplicito = !!(os?.maquinaVoltouRodarEm || os?.maquinaParadaEncerradaEm);
          const possuiEstadoAtual = typeof os?.maquinaParadaAtiva === "boolean";
          const possuiUltimo = os?.maquinaContinuaParadaUltimo != null;
          return !possuiFimExplicito && !possuiEstadoAtual && !possuiUltimo;
        });

        if (requerFallbackAtendimentos) {
          try {
            const atendRef = collection(db, "atendimentos");
            const qa = query(atendRef, where("grupoId", "==", grupoId), limit(3000));
            const atSnap = await getDocs(qa);
            if (isStale()) return;

            // Mapa para compatibilizar atendimentos que guardam apenas o código (ex: "OS0005")
            // em vez do osId (docId do Firestore).
            const codigoToId = new Map();
            for (const os of lista) {
              const cod = String(os?.codigo || os?.codigoOs || os?.osCodigo || "").trim();
              if (cod) codigoToId.set(cod, os.id);
            }

            const lastByOs = new Map();
            // Para OS antigas: precisamos do PRIMEIRO atendimento que marcou "Não".
            // Se usarmos o último, uma OS que ficou aberta por dias (ex: aguardando material)
            // vira uma "parada" gigante, mesmo que a máquina tenha voltado a rodar no 1º atendimento.
            const firstNaoByOs = new Map();
            for (const docu of atSnap.docs) {
              const a = docu.data() || {};

            // Pode vir como osId (preferencial) ou como osCodigo (export mostra isso)
              let osId = a.osId || null;
              const osCodigo = a.osCodigo || a.codigoOs || a.codigo || null;
              if (!osId && osCodigo) osId = codigoToId.get(String(osCodigo).trim()) || null;
              if (!osId) continue;

            // Datas possíveis (modelos antigos variam)
              const fim =
                a.dataFim ||
                a.horarioFim ||
                a.atualizadoEm ||
                a.updatedAt ||
                a.updatedEm ||
                a.criadoEm ||
                null;
              const fimD = tsToDate(fim);
              if (!fimD) continue;

              const prev = lastByOs.get(osId);
              if (!prev || fimD.getTime() > prev.fimD.getTime()) {
                lastByOs.set(osId, { fim, fimD, continua: a.maquinaContinuaParada });
              }

            // Captura o PRIMEIRO "Não" (fim real da parada, para o primeiro ciclo)
              const ult = String(a.maquinaContinuaParada ?? "").toLowerCase().trim();
              const disseNao = ["nao", "não", "n", "false", "0", "no"].includes(ult);
              if (disseNao) {
                const prevNao = firstNaoByOs.get(osId);
                if (!prevNao || fimD.getTime() < prevNao.fimD.getTime()) {
                  firstNaoByOs.set(osId, { fim, fimD });
                }
              }
            }

            for (const os of lista) {
              if (!os?.id) continue;
              const last = lastByOs.get(os.id);
              if (!last) continue;

            // Sempre guarda referência do último atendimento (para pickParadaFim)
              os.__lastAtendimentoFim = last.fim;
              os.__lastAtendimentoContinua = last.continua;

            // Se a OS não tem o campo "ultimo", usa o do atendimento
              if (os.maquinaContinuaParadaUltimo == null && last.continua != null) {
                os.maquinaContinuaParadaUltimo = last.continua;
              }

            // Se o último atendimento disse NÃO, usar o fim do atendimento como "fim da parada"
            // mesmo que existam campos antigos inconsistentes (ex: maquinaParadaAtiva=true).
              const firstNao = firstNaoByOs.get(os.id);
              if (firstNao && !os.maquinaVoltouRodarEm && !os.maquinaParadaEncerradaEm) {
                os.__derivedParadaFim = firstNao.fim;
              }
            }
          } catch (eAt) {
            // Se der erro (índice/limite), seguimos sem o fallback e usamos apenas os campos da OS.
            console.warn("Dashboard: fallback de atendimentos indisponível", eAt);
          }
        }

        const now = new Date();
        const curStart = startOfMonth(now);
        const curEnd = endOfMonth(now);
        const prevRef = new Date(curStart.getTime() - 1);
        const prevStart = startOfMonth(prevRef);
        const prevEnd = endOfMonth(prevRef);

        const inCurMonth = (os) => {
          const c = tsToDate(os?.criadoEm);
          if (!c) return false;
          return c.getTime() >= curStart.getTime() && c.getTime() <= curEnd.getTime();
        };
        const inPrevMonth = (os) => {
          const c = tsToDate(os?.criadoEm);
          if (!c) return false;
          return c.getTime() >= prevStart.getTime() && c.getTime() <= prevEnd.getTime();
        };

        // OS abertas (todas) — usado no card "OS em aberto" do Dashboard
        // (não restringe a máquinas-chave)
        const abertasAgoraAll = lista.filter((os) => isAbertaEm(os, now));
        const abertasNoFimPrevAll = lista.filter((os) => isAbertaEm(os, prevEnd));

        // OS abertas (somente máquinas-chave) — base para KPIs de parada/MTBF/MTTR
        const abertasAgora = baseLista.filter((os) => isAbertaEm(os, now));
        const abertasNoFimPrev = baseLista.filter((os) => isAbertaEm(os, prevEnd));

        const paradasAgora = abertasAgora.filter(isMaquinaParadaAgora);
        const paradasNoFimPrev = abertasNoFimPrev.filter(isMaquinaParadaAgora);

        const maquinasParadasAgora = new Set(
          paradasAgora.map((os) => String(os.maquinaId || os.maquinaNome || "(Sem máquina)"))
        ).size;
        const maquinasParadasAgoraPrev = new Set(
          paradasNoFimPrev.map((os) => String(os.maquinaId || os.maquinaNome || "(Sem máquina)"))
        ).size;

        const aguardAgora = abertasAgora.filter(isAguardandoMaterial).length;
        const aguardPrev = abertasNoFimPrev.filter(isAguardandoMaterial).length;

        // OS criadas no mês (todas) — card "OS abertas no mês"
        const abertasMesAll = lista.filter(inCurMonth);
        const abertasMesPrevAll = lista.filter(inPrevMonth);

        // Finalizadas no mês (aproximação por status bloqueado + atualizadoEm dentro do mês)
        const finalizadasMes = baseLista.filter((os) => {
          if (!isStatusBloqueado(os?.statusNome)) return false;
          const endD = tsToDate(pickEndTs(os));
          if (!endD) return false;
          return endD.getTime() >= curStart.getTime() && endD.getTime() <= curEnd.getTime();
        });
        const finalizadasMesPrev = baseLista.filter((os) => {
          if (!isStatusBloqueado(os?.statusNome)) return false;
          const endD = tsToDate(pickEndTs(os));
          if (!endD) return false;
          return endD.getTime() >= prevStart.getTime() && endD.getTime() <= prevEnd.getTime();
        });

        // Paradas (h) no mês: soma de duração das OS do mês com máquina parada
        const paradasMesList = baseLista.filter((os) => inCurMonth(os) && isMaquinaParada(os));
        const paradasMesPrevList = baseLista.filter((os) => inPrevMonth(os) && isMaquinaParada(os));

        const duracaoOs = (os, refNow) => {
          const ini = pickParadaInicio(os);
          const fim = pickParadaFim(os, refNow);
          return hoursBetween(ini, fim);
        };

        const paradasHorasMes = paradasMesList.reduce((acc, os) => acc + duracaoOs(os, now), 0);
        const paradasHorasMesPrev = paradasMesPrevList.reduce((acc, os) => acc + duracaoOs(os, prevEnd), 0);

        // MTTR (mês): média da duração das OS finalizadas no mês onde máquina parou
        const finalizadasParouMes = finalizadasMes.filter(isMaquinaParada);
        const mttrHorasMes = finalizadasParouMes.length
          ? finalizadasParouMes.reduce((acc, os) => acc + duracaoOs(os, now), 0) / finalizadasParouMes.length
          : null;

        // MTBF (mês): MTBF Global (frota) considerando SOMENTE máquinas-chave
        // Fórmula: (horas do mês * qtd máquinas-chave) / qtd falhas (OS com máquina parada criadas no mês nas máquinas-chave)
        const horasNoMes = hoursBetween(curStart, curEnd);
        const falhasMes = paradasMesList.length;
        const totalHorasFrota = maquinasChave.length ? horasNoMes * maquinasChave.length : 0;
        const mtbfHorasMes = falhasMes ? totalHorasFrota / falhasMes : null;

        // Maior parada (evento) no mês
        const maiorParadaHorasMes = paradasMesList.reduce((acc, os) => Math.max(acc, duracaoOs(os, now)), 0);

        // Máquina mais crítica do mês (por horas paradas)
        const mapH = new Map();
        for (const os of paradasMesList) {
          const key = String(os.maquinaNome || os.maquinaId || "(Sem máquina)");
          mapH.set(key, (mapH.get(key) || 0) + duracaoOs(os, now));
        }
        const maquinaMaisCriticaMes = Array.from(mapH.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || "-";

        // Top 3 OS críticas abertas (por duração)
        const top = paradasAgora
          .map((os) => ({
            id: os.id,
            codigo: os.codigo || os.codigoOs || os.id,
            maquina: os.maquinaNome || "(Sem máquina)",
            titulo: os.titulo || os.descricao || "Sem descrição",
            status: os.statusNome || "-",
            criticidade: os.criticidade || "-",
            horas: duracaoOs(os, now),
          }))
          .sort((a, b) => b.horas - a.horas)
          .slice(0, 3);

        const nextKpis = {
          paradasHorasMes,
          paradasHorasMesPrev,
          maquinasParadasAgora,
          maquinasParadasAgoraPrev,
          osAbertasAgora: abertasAgoraAll.length,
          osAbertasAgoraPrev: abertasNoFimPrevAll.length,
          osAbertasMes: abertasMesAll.length,
          osAbertasMesPrev: abertasMesPrevAll.length,
          osFinalizadasMes: finalizadasMes.length,
          osFinalizadasMesPrev: finalizadasMesPrev.length,
          aguardandoMaterialAgora: aguardAgora,
          aguardandoMaterialAgoraPrev: aguardPrev,
          mtbfHorasMes,
          mttrHorasMes,
          maiorParadaHorasMes,
          maquinaMaisCriticaMes,
        };

        if (!isStale()) {
          setTopOsAbertas(top);
          setKpis(nextKpis);
          dashboardCache.set(cacheKey, {
            at: Date.now(),
            topOsAbertas: top,
            kpis: nextKpis,
          });
        }
      } catch (e) {
        console.error(e);
        if (!isStale()) {
          setError("Erro ao carregar o Dashboard. Verifique as permissões/consultas do Firestore.");
        }
      } finally {
        if (!isStale()) {
          setLoading(false);
        }
      }
    }

    run();

    return () => {
      requestRef.current += 1;
    };
  }, [canLoad, grupoId]);

  const deltas = useMemo(() => {
    return {
      paradasHorasMes: pctDelta(kpis.paradasHorasMes, kpis.paradasHorasMesPrev),
      maquinasParadasAgora: pctDelta(kpis.maquinasParadasAgora, kpis.maquinasParadasAgoraPrev),
      osAbertasAgora: pctDelta(kpis.osAbertasAgora, kpis.osAbertasAgoraPrev),
      osAbertasMes: pctDelta(kpis.osAbertasMes, kpis.osAbertasMesPrev),
      osFinalizadasMes: pctDelta(kpis.osFinalizadasMes, kpis.osFinalizadasMesPrev),
      aguardandoMaterialAgora: pctDelta(kpis.aguardandoMaterialAgora, kpis.aguardandoMaterialAgoraPrev),
    };
  }, [kpis]);

  return { loading, error, kpis, deltas, topOsAbertas };
}
